/**
 * @deprecated
 * IterationTimeline - Visual timeline of iterations with decision indicators
 * Shows iteration progression with quality scores and selection state
 */

import React from 'react';
import { CheckCircle, XCircle, RotateCcw, Clock, Star } from 'lucide-react';

interface IterationTimelineProps {
  iterations: IterationData[];
  maxIterations: number;
  currentIteration: number;
  compact?: boolean;
  onIterationClick?: (iteration: IterationData) => void;
}

interface IterationData {
  id: string;
  iteration_number: number;
  content: any;
  scores: {
    actual: number;
    threshold: number;
    decision: 'accept' | 'improve' | 'reject';
  };
  cost: number;
  generated_at: Date;
  is_selected: boolean;
}

const IterationTimeline: React.FC<IterationTimelineProps> = ({
  iterations,
  maxIterations,
  currentIteration,
  compact = false,
  onIterationClick
}) => {
  const getDecisionIcon = (decision: 'accept' | 'improve' | 'reject') => {
    switch (decision) {
      case 'accept':
        return <CheckCircle className="w-3 h-3" />;
      case 'improve':
        return <RotateCcw className="w-3 h-3" />;
      case 'reject':
        return <XCircle className="w-3 h-3" />;
      default:
        return <Clock className="w-3 h-3" />;
    }
  };

  const getDecisionColor = (iteration: IterationData | undefined, index: number) => {
    if (!iteration) {
      return 'bg-gray-200 border-gray-300';
    }

    if (iteration.is_selected) {
      return 'bg-blue-500 border-blue-600 ring-2 ring-blue-300';
    }

    switch (iteration.scores.decision) {
      case 'accept':
        return 'bg-green-500 border-green-600';
      case 'improve':
        return 'bg-yellow-500 border-yellow-600';
      case 'reject':
        return 'bg-red-500 border-red-600';
      default:
        return index < currentIteration ? 'bg-blue-400 border-blue-500' : 'bg-gray-300 border-gray-400';
    }
  };

  const getTooltipContent = (iteration: IterationData) => {
    return `Iteration ${iteration.iteration_number}
Score: ${iteration.scores.actual.toFixed(2)} (${iteration.scores.decision})
Cost: $${iteration.cost.toFixed(4)}
Generated: ${iteration.generated_at.toLocaleTimeString()}${
      iteration.is_selected ? '\n⭐ Selected' : ''
    }`;
  };

  if (compact) {
    return (
      <div className="flex items-center space-x-1">
        {Array.from({ length: maxIterations }).map((_, index) => {
          const iteration = iterations.find(it => it.iteration_number === index + 1);
          const isActive = index + 1 === currentIteration;
          
          return (
            <div
              key={index}
              className={`relative w-6 h-6 rounded-full border-2 transition-all duration-300 cursor-pointer hover:scale-110 ${
                getDecisionColor(iteration, index + 1)
              } ${isActive ? 'animate-pulse scale-110' : ''}`}
              title={iteration ? getTooltipContent(iteration) : `Iteration ${index + 1}`}
              onClick={() => iteration && onIterationClick?.(iteration)}
            >
              <div className="flex items-center justify-center text-white">
                {iteration ? (
                  <>
                    {iteration.is_selected && (
                      <Star className="w-3 h-3 fill-current" />
                    )}
                    {!iteration.is_selected && getDecisionIcon(iteration.scores.decision)}
                  </>
                ) : (
                  <span className="text-xs font-bold">{index + 1}</span>
                )}
              </div>

              {/* Connector line */}
              {index < maxIterations - 1 && (
                <div
                  className={`absolute left-6 top-3 w-4 h-0.5 transition-colors duration-300 ${
                    index + 1 < currentIteration ? 'bg-green-400' : 'bg-gray-300'
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Full timeline view
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-600">
          Progress: {currentIteration} of {maxIterations} iterations
        </span>
        {iterations.some(it => it.is_selected) && (
          <span className="text-sm text-blue-600 font-medium flex items-center">
            <Star className="w-4 h-4 mr-1 fill-current" />
            Selected Output Available
          </span>
        )}
      </div>

      <div className="flex items-center space-x-3 overflow-x-auto pb-2">
        {Array.from({ length: maxIterations }).map((_, index) => {
          const iteration = iterations.find(it => it.iteration_number === index + 1);
          const isActive = index + 1 === currentIteration;
          const isPending = index + 1 > currentIteration;
          
          return (
            <div key={index} className="relative group flex-shrink-0">
              {/* Iteration node */}
              <div
                className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all duration-300 cursor-pointer hover:scale-105 ${
                  getDecisionColor(iteration, index + 1)
                } ${isActive ? 'animate-pulse scale-110 shadow-lg' : ''} ${isPending ? 'opacity-50' : ''}`}
                onClick={() => iteration && onIterationClick?.(iteration)}
              >
                <div className="flex flex-col items-center text-white">
                  {iteration ? (
                    <>
                      {iteration.is_selected && (
                        <Star className="w-4 h-4 fill-current" />
                      )}
                      {!iteration.is_selected && getDecisionIcon(iteration.scores.decision)}
                    </>
                  ) : (
                    <span className="text-sm font-bold">{index + 1}</span>
                  )}
                </div>
              </div>

              {/* Connector line */}
              {index < maxIterations - 1 && (
                <div
                  className={`absolute left-12 top-6 w-8 h-0.5 transition-colors duration-300 ${
                    index + 1 < currentIteration ? 'bg-green-400' : 'bg-gray-300'
                  }`}
                />
              )}

              {/* Iteration details on hover */}
              {iteration && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                  <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
                    <div className="font-semibold mb-1">
                      Iteration {iteration.iteration_number}
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span>Score:</span>
                        <span className="font-mono">{iteration.scores.actual.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Decision:</span>
                        <span className="capitalize">{iteration.scores.decision}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Cost:</span>
                        <span className="font-mono">${iteration.cost.toFixed(4)}</span>
                      </div>
                      <div className="text-xs text-gray-300">
                        {iteration.generated_at.toLocaleTimeString()}
                      </div>
                      {iteration.is_selected && (
                        <div className="text-xs text-blue-300 font-medium">
                          ⭐ Selected Output
                        </div>
                      )}
                    </div>
                    
                    {/* Arrow */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                      <div className="border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Iteration label */}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 text-xs text-center">
                <div className="text-gray-600 font-medium">#{index + 1}</div>
                {iteration && (
                  <div className={`font-mono text-xs mt-1 ${
                    iteration.scores.actual >= iteration.scores.threshold 
                      ? 'text-green-600' 
                      : 'text-yellow-600'
                  }`}>
                    {iteration.scores.actual.toFixed(2)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className="relative">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-500"
            style={{ width: `${(currentIteration / maxIterations) * 100}%` }}
          />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-medium text-gray-700 bg-white px-2 rounded">
            {Math.round((currentIteration / maxIterations) * 100)}%
          </span>
        </div>
      </div>
    </div>
  );
};

export default IterationTimeline;